'use client';
import Icon from '@/components/common/Icon';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import PropTypes from 'prop-types';
import styles from './BottomTabBar.module.scss';

/**
 * BottomTabBar Component
 * Modern mobile navigation following 2026+ iOS/Android patterns
 * Sticky bottom bar with 5 primary tabs
 *
 * @param {Function} setMoreMenuOpen - Function to open the More menu
 */
const BottomTabBar = ({ setMoreMenuOpen }) => {
  const pathname = usePathname();

  // Define primary navigation tabs (4 main + 1 More)
  const tabs = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: 'DashboardOutlined',
      path: '/account/dashboard',
    },
    {
      key: 'tools',
      label: 'Tools',
      icon: 'Tool',
      path: '/account/tools',
      matchPaths: ['/account/tools', '/account/bimi-checker', '/account/dmarc-inspector'],
    },
    {
      key: 'orders',
      label: 'Orders',
      icon: 'ShoppingOutlined',
      path: '/account/orders',
    },
    {
      key: 'buy-now',
      label: 'Buy Now',
      icon: 'ShoppingCartOutlined',
      path: '/account/buy-now',
    },
    {
      key: 'more',
      label: 'More',
      icon: 'EllipsisOutlined',
      onClick: () => setMoreMenuOpen(true),
    },
  ];

  // Check if a tab is active
  const isActive = (tab) => {
    if (tab.key === 'more') return false; // More is never "active"

    if (tab.matchPaths) {
      return tab.matchPaths.some((path) => pathname?.startsWith(path));
    }

    return pathname === tab.path || pathname?.startsWith(`${tab.path}/`);
  };

  return (
    <nav className={styles.bottomTabBar}>
      {tabs.map((tab) => {
        const active = isActive(tab);

        // If tab has onClick (More menu), render as button
        if (tab.onClick) {
          return (
            <button
              key={tab.key}
              className={`${styles.tabItem} ${active ? styles.active : ''}`}
              onClick={tab.onClick}
              aria-label={tab.label}
            >
              <Icon name={tab.icon} size={24} />
              <span className={styles.tabLabel}>{tab.label}</span>
            </button>
          );
        }

        // Otherwise render as link
        return (
          <Link
            key={tab.key}
            href={tab.path}
            className={`${styles.tabItem} ${active ? styles.active : ''}`}
            target={tab.isExternal ? '_blank' : undefined}
            rel={tab.isExternal ? 'noopener noreferrer' : undefined}
          >
            <Icon name={tab.icon} size={24} />
            <span className={styles.tabLabel}>{tab.label}</span>
          </Link>
        );
      })}
    </nav>
  );
};

BottomTabBar.propTypes = {
  setMoreMenuOpen: PropTypes.func.isRequired,
};

export default BottomTabBar;
